import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, setDoc, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { db } from "../firebase";
import ShopDashboard from "./ShopDashboard";
import ShopSuggestions from "./ShopSuggestions";
import ConfirmDialog from "./ConfirmDialog";
import Toast from "./Toast";

const SuperAdminDashboard = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShop, setSelectedShop] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list", "shop-dashboard", "pending", "suggestions"
  const [confirmState, setConfirmState] = useState(null); // { title, message, danger, requireTypedText, confirmLabel, onConfirm }
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => setToast({ message, type });
  const [message, setMessage] = useState("");
  const [showCustomPricing, setShowCustomPricing] = useState(false);
  const [customPricingShop, setCustomPricingShop] = useState(null);
  const [customPricing, setCustomPricing] = useState({
    monthlyPrice: "",
    yearlyPrice: "",
    isCustomPricing: false
  });
  // Owner linking UX state
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [ownerModalShop, setOwnerModalShop] = useState(null);
  const [ownerSearchQuery, setOwnerSearchQuery] = useState("");
  const [ownerSearchResults, setOwnerSearchResults] = useState([]);
  const [ownerSearching, setOwnerSearching] = useState(false);

  // Load all shops
  useEffect(() => {
    loadShops();
  }, []);

  const loadShops = async () => {
    setLoading(true);
    try {
      const shopsRef = collection(db, "shops");
      const querySnapshot = await getDocs(shopsRef);
      
      const shopsData = [];
      querySnapshot.forEach((doc) => {
        shopsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setShops(shopsData);
      setMessage(`Winkels geladen: ${shopsData.length} winkel${shopsData.length === 1 ? '' : 's'} gevonden`);
    } catch (error) {
      console.error("Error loading shops:", error);
      setMessage("Fout bij laden winkels: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingShops = async () => {
    setLoading(true);
    try {
      const shopsRef = collection(db, "shops");
      const querySnapshot = await getDocs(shopsRef);
      const pending = [];
      querySnapshot.forEach((d) => {
        const data = d.data();
        if (data && (data.isVerified === false || data.isActive === false)) {
          pending.push({ id: d.id, ...data });
        }
      });
      setShops(pending);
      setMessage(`Aanmeldingen geladen: ${pending.length}`);
    } catch (error) {
      setMessage("Fout bij laden aanmeldingen: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const approveShop = (shop) => {
    setConfirmState({
      title: "Winkel goedkeuren?",
      message: `Winkel "${shop.name || shop.id}" goedkeuren en toegang geven?`,
      confirmLabel: "Goedkeuren",
      onConfirm: () => doApproveShop(shop),
    });
  };

  const doApproveShop = async (shop) => {
    setConfirmState(null);
    try {
      setMessage("Winkel goedkeuren...");
      await updateDoc(doc(db, "shops", shop.id), { isActive: true, isVerified: true });

      // Koppel eigenaar op basis van shop.email indien bekend
      if (shop.email) {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("email", "==", (shop.email || "").trim().toLowerCase()));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const uid = snap.docs[0].id;
          await setDoc(doc(db, "users", uid), { isAdmin: true, adminShopId: shop.id }, { merge: true });
          setMessage(`Winkel goedgekeurd en eigenaar gekoppeld (${shop.email}).`);
        } else {
          setMessage("Winkel goedgekeurd. Geen eigenaar gevonden op e‑mail; koppel handmatig via ‘Koppel eigenaar’.");
        }
      } else {
        setMessage("Winkel goedgekeurd. Geen e‑mail bekend; koppel eigenaar via ‘Koppel eigenaar’.");
      }

      if (viewMode === "pending") {
        await loadPendingShops();
      } else {
        await loadShops();
      }
    } catch (error) {
      setMessage("Fout bij goedkeuren: " + error.message);
    }
  };

  const selectShop = (shop) => {
    setSelectedShop(shop);
    setViewMode("shop-dashboard");
  };

  const backToList = () => {
    setSelectedShop(null);
    setViewMode("list");
  };

  const deleteShop = (shopId, shopName) => {
    setConfirmState({
      title: "Winkel verwijderen",
      message: `Weet je zeker dat je winkel "${shopName}" inclusief alle data (klanten, foto's, feed-posts) wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`,
      danger: true,
      requireTypedText: shopName || shopId,
      confirmLabel: "Verwijderen",
      onConfirm: () => doDeleteShop(shopId, shopName),
    });
  };

  const doDeleteShop = async (shopId, shopName) => {
    setConfirmState(null);
    try {
      // Gebruik cascade delete Cloud Function (alleen super admins)
      setMessage("Winkel en gerelateerde data verwijderen...");
      const cascade = httpsCallable(functions, "cascadeDeleteShop");
      await cascade({ shopId });
      showToast(`Winkel "${shopName}" en gerelateerde data verwijderd.`, "success");
      setMessage("");
      loadShops(); // Reload shops
    } catch (error) {
      showToast("Fout bij verwijderen winkel: " + error.message, "error");
    }
  };

  const assignOwnerToShop = async (shopId, shopName) => {
    const ownerEmail = window.prompt(`Voer het e‑mailadres in van de eigenaar voor "${shopName}"`);
    if (!ownerEmail) return;

    try {
      setMessage("Eigenaar koppelen...");
      // Vind user op e‑mail
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", ownerEmail.trim().toLowerCase()));
      const snap = await getDocs(q);

      if (snap.empty) {
        setMessage(`Geen gebruiker gevonden met e‑mail: ${ownerEmail}`);
        return;
      }

      const userDoc = snap.docs[0];
      const uid = userDoc.id;

      // Zet user als admin en koppel aan shop
      await setDoc(doc(db, "users", uid), {
        isAdmin: true,
        adminShopId: shopId
      }, { merge: true });

      // Schrijf e‑mail naar shop document (handig voor overzicht)
      await updateDoc(doc(db, "shops", shopId), {
        email: ownerEmail.trim().toLowerCase()
      });

      setMessage(`Eigenaar gekoppeld en e‑mail ingesteld voor "${shopName}"`);
      await loadShops();
    } catch (error) {
      console.error("Assign owner error:", error);
      setMessage("Fout bij koppelen eigenaar: " + error.message);
    }
  };

  // Improved owner linking UX
  const openOwnerModal = (shop) => {
    setOwnerModalShop(shop);
    setOwnerSearchQuery("");
    setOwnerSearchResults([]);
    setShowOwnerModal(true);
  };

  const closeOwnerModal = () => {
    setShowOwnerModal(false);
    setOwnerModalShop(null);
    setOwnerSearchQuery("");
    setOwnerSearchResults([]);
  };

  const searchOwners = async () => {
    if (!ownerSearchQuery.trim()) {
      setOwnerSearchResults([]);
      return;
    }
    setOwnerSearching(true);
    try {
      const usersRef = collection(db, "users");
      const snap = await getDocs(usersRef);
      const q = ownerSearchQuery.toLowerCase();
      const results = [];
      snap.forEach((d) => {
        const ud = d.data();
        const name = `${ud.firstName || ""} ${ud.lastName || ""}`.toLowerCase();
        const email = (ud.email || "").toLowerCase();
        if (name.includes(q) || email.includes(q)) {
          results.push({ id: d.id, ...ud });
        }
      });
      setOwnerSearchResults(results);
    } catch (e) {
      setMessage("Fout bij zoeken: " + e.message);
      setOwnerSearchResults([]);
    }
    setOwnerSearching(false);
  };

  const linkOwner = (user) => {
    if (!ownerModalShop) return;
    setConfirmState({
      title: "Eigenaar koppelen?",
      message: `Koppel ${user.email || user.firstName || user.id} als eigenaar van "${ownerModalShop.name || ownerModalShop.id}"?`,
      confirmLabel: "Koppelen",
      onConfirm: () => doLinkOwner(user),
    });
  };

  const doLinkOwner = async (user) => {
    setConfirmState(null);
    if (!ownerModalShop) return;
    try {
      await setDoc(doc(db, "users", user.id), {
        isAdmin: true,
        adminShopId: ownerModalShop.id
      }, { merge: true });
      await updateDoc(doc(db, "shops", ownerModalShop.id), {
        email: (user.email || "").toLowerCase()
      });
      showToast("Eigenaar gekoppeld.", "success");
      closeOwnerModal();
      await loadShops();
    } catch (e) {
      showToast("Fout bij koppelen eigenaar: " + e.message, "error");
    }
  };

  const unlinkOwner = (shop) => {
    setConfirmState({
      title: "Eigenaar ontkoppelen?",
      message: `Eigenaar ontkoppelen van "${shop.name || shop.id}"?`,
      confirmLabel: "Ontkoppelen",
      onConfirm: () => doUnlinkOwner(shop),
    });
  };

  const doUnlinkOwner = async (shop) => {
    setConfirmState(null);
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("adminShopId", "==", shop.id));
      const snap = await getDocs(q);
      for (const u of snap.docs) {
        await setDoc(doc(db, "users", u.id), { isAdmin: false, adminShopId: null }, { merge: true });
      }
      await updateDoc(doc(db, "shops", shop.id), { email: shop.email || "" });
      showToast("Eigenaar ontkoppeld (indien aanwezig).", "success");
      await loadShops();
    } catch (e) {
      showToast("Fout bij ontkoppelen eigenaar: " + e.message, "error");
    }
  };

  const toggleShopPlan = (shopId, shopName, currentPlan) => {
    const newPlan = currentPlan === "premium" ? "free" : "premium";
    const planText = newPlan === "premium" ? "Premium" : "Gratis";
    setConfirmState({
      title: "Plan wijzigen?",
      message: `Weet je zeker dat je winkel "${shopName}" wilt wijzigen naar ${planText} plan?`,
      confirmLabel: "Wijzigen",
      onConfirm: () => doToggleShopPlan(shopId, shopName, newPlan, planText),
    });
  };

  const doToggleShopPlan = async (shopId, shopName, newPlan, planText) => {
    setConfirmState(null);
    try {
      await updateDoc(doc(db, "shops", shopId), {
        plan: newPlan,
        planUpdatedAt: new Date(),
        planUpdatedBy: "superadmin"
      });
      showToast(`Winkel "${shopName}" gewijzigd naar ${planText} plan.`, "success");
      loadShops(); // Reload shops
    } catch (error) {
      showToast("Fout bij wijzigen plan: " + error.message, "error");
    }
  };

  const openCustomPricing = (shop) => {
    setCustomPricingShop(shop);
    setCustomPricing({
      monthlyPrice: shop.customMonthlyPrice || "",
      yearlyPrice: shop.customYearlyPrice || "",
      isCustomPricing: shop.isCustomPricing || false
    });
    setShowCustomPricing(true);
  };

  const saveCustomPricing = async () => {
    if (!customPricingShop) return;

    try {
      const updateData = {
        customMonthlyPrice: parseFloat(customPricing.monthlyPrice) || null,
        customYearlyPrice: parseFloat(customPricing.yearlyPrice) || null,
        isCustomPricing: customPricing.isCustomPricing,
        customPricingUpdatedAt: new Date(),
        customPricingUpdatedBy: "superadmin"
      };

      await updateDoc(doc(db, "shops", customPricingShop.id), updateData);

      showToast(`Custom pricing voor "${customPricingShop.name}" opgeslagen.`, "success");
      setShowCustomPricing(false);
      setCustomPricingShop(null);
      loadShops(); // Reload shops
    } catch (error) {
      showToast("Fout bij opslaan custom pricing: " + error.message, "error");
    }
  };

  const removeCustomPricing = () => {
    if (!customPricingShop) return;
    setConfirmState({
      title: "Custom pricing verwijderen?",
      message: `Weet je zeker dat je custom pricing voor "${customPricingShop.name}" wilt verwijderen?`,
      danger: true,
      confirmLabel: "Verwijderen",
      onConfirm: doRemoveCustomPricing,
    });
  };

  const doRemoveCustomPricing = async () => {
    setConfirmState(null);
    if (!customPricingShop) return;
    try {
      await updateDoc(doc(db, "shops", customPricingShop.id), {
        customMonthlyPrice: null,
        customYearlyPrice: null,
        isCustomPricing: false,
        customPricingUpdatedAt: new Date(),
        customPricingUpdatedBy: "superadmin"
      });

      showToast(`Custom pricing voor "${customPricingShop.name}" verwijderd.`, "success");
      setShowCustomPricing(false);
      setCustomPricingShop(null);
      loadShops(); // Reload shops
    } catch (error) {
      showToast("Fout bij verwijderen custom pricing: " + error.message, "error");
    }
  };

  // Aangedragen zaken (community suggesties) bekijken en beoordelen
  if (viewMode === "suggestions") {
    return (
      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          paddingBottom: "20px",
          borderBottom: "2px solid #e9ecef"
        }}>
          <h1 style={{ margin: 0, color: "#333" }}>Cupdate SuperAdmin Dashboard</h1>
          <button
            onClick={() => setViewMode("list")}
            style={{
              padding: "10px 20px",
              background: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer"
            }}
          >
            Terug naar winkels
          </button>
        </div>
        <ShopSuggestions />
      </div>
    );
  }

  // If a shop is selected, show the ShopDashboard
  if (viewMode === "shop-dashboard" && selectedShop) {
    return (
      <ShopDashboard 
        shopId={selectedShop.id}
        onBack={backToList}
      />
    );
  }

  // Custom Pricing Modal
  if (showCustomPricing && customPricingShop) {
    return (
      <>
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000
      }}>
        <div role="dialog" aria-modal="true" aria-labelledby="customPricingTitle" style={{
          background: "white",
          padding: "30px",
          borderRadius: "12px",
          maxWidth: "500px",
          width: "90%",
          maxHeight: "80vh",
          overflowY: "auto"
        }}>
          <h2 id="customPricingTitle" style={{ marginBottom: "20px", color: "#333" }}>
            Custom Pricing - {customPricingShop.name}
          </h2>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              <input
                type="checkbox"
                checked={customPricing.isCustomPricing}
                onChange={(e) => setCustomPricing({
                  ...customPricing,
                  isCustomPricing: e.target.checked
                })}
                style={{ marginRight: "8px" }}
              />
              Custom Pricing Activeren
            </label>
          </div>

          {customPricing.isCustomPricing && (
            <>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                  Maandelijkse Prijs (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={customPricing.monthlyPrice}
                  onChange={(e) => setCustomPricing({
                    ...customPricing,
                    monthlyPrice: e.target.value
                  })}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    fontSize: "16px"
                  }}
                  placeholder="6.99"
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                  Jaarlijkse Prijs (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={customPricing.yearlyPrice}
                  onChange={(e) => setCustomPricing({
                    ...customPricing,
                    yearlyPrice: e.target.value
                  })}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    fontSize: "16px"
                  }}
                  placeholder="50.00"
                />
              </div>
            </>
          )}

          <div style={{
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            marginTop: "20px"
          }}>
            <button
              onClick={() => {
                setShowCustomPricing(false);
                setCustomPricingShop(null);
              }}
              style={{
                padding: "10px 20px",
                background: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer"
              }}
              aria-label="Sluit custom pricing"
            >
              Annuleren
            </button>
            
            {customPricingShop.isCustomPricing && (
              <button
                onClick={removeCustomPricing}
                style={{
                  padding: "10px 20px",
                  background: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer"
                }}
                aria-label="Verwijder custom pricing"
              >
                Verwijderen
              </button>
            )}
            
            <button
              onClick={saveCustomPricing}
              style={{
                padding: "10px 20px",
                background: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer"
              }}
              aria-label="Sla custom pricing op"
            >
              Opslaan
            </button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        danger={confirmState?.danger}
        requireTypedText={confirmState?.requireTypedText}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
      </>
    );
  }

  // Owner Linking Modal
  if (showOwnerModal && ownerModalShop) {
    return (
      <>
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000
      }}>
        <div style={{
          background: "white",
          padding: "30px",
          borderRadius: "12px",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "80vh",
          overflowY: "auto"
        }}>
          <h2 style={{ marginBottom: "12px", color: "#333" }}>Koppel eigenaar</h2>
          <p style={{ marginTop: 0, color: "#666" }}>Winkel: <strong>{ownerModalShop.name || ownerModalShop.id}</strong></p>

          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <input
              type="text"
              placeholder="Zoek op naam of e‑mail..."
              value={ownerSearchQuery}
              onChange={(e) => setOwnerSearchQuery(e.target.value)}
              className="form-input"
              style={{ flex: 1 }}
            />
            <button onClick={searchOwners} className="btn btn-secondary" disabled={ownerSearching}>
              {ownerSearching ? "Zoeken..." : "Zoeken"}
            </button>
          </div>

          {ownerSearchResults.length > 0 ? (
            <div style={{ maxHeight: "280px", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px" }}>
              {ownerSearchResults.map((user) => (
                <div key={user.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px", borderBottom: "1px solid #f2f2f2" }}>
                  <div>
                    <strong>{user.firstName} {user.lastName}</strong>
                    <div style={{ fontSize: "12px", color: "#666" }}>{user.email}</div>
                  </div>
                  <button onClick={() => linkOwner(user)} className="btn btn-success" style={{ fontSize: "12px" }}>Koppelen</button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#666", fontSize: "14px", marginBottom: "8px" }}>Geen resultaten</div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "16px" }}>
            <button onClick={() => unlinkOwner(ownerModalShop)} className="btn btn-warning">Ontkoppel eigenaar</button>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={closeOwnerModal} className="btn btn-secondary">Sluiten</button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        danger={confirmState?.danger}
        requireTypedText={confirmState?.requireTypedText}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
      </>
    );
  }

  return (
    <>
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "30px",
        paddingBottom: "20px",
        borderBottom: "2px solid #e9ecef"
      }}>
        <h1 style={{ margin: 0, color: "#333" }}>Cupdate SuperAdmin Dashboard</h1>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={async () => {
              if (viewMode === "pending") {
                setViewMode("list");
                await loadShops();
              } else {
                setViewMode("pending");
                await loadPendingShops();
              }
            }}
            style={{
              padding: "10px 20px",
              background: viewMode === "pending" ? "#6c757d" : "#17a2b8",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer"
            }}
          >
            {viewMode === "pending" ? "Alle Winkels" : "Aanmeldingen"}
          </button>
          <button
            onClick={() => setViewMode("suggestions")}
            style={{
              padding: "10px 20px",
              background: "#3A4A68",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer"
            }}
          >
            📬 Aangedragen zaken
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div role="status" aria-live="polite" style={{
          padding: "15px",
          borderRadius: "5px",
          marginBottom: "20px",
          background: "#d4edda",
          border: "1px solid #c3e6cb",
          color: "#155724"
        }}>
          {message}
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "20px",
        marginBottom: "30px"
      }}>
        <div style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          textAlign: "center"
        }}>
          <h3 style={{ margin: "0 0 10px 0", color: "#007bff" }}>Totaal Winkels</h3>
          <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>{shops.length}</p>
        </div>
        <div style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          textAlign: "center"
        }}>
          <h3 style={{ margin: "0 0 10px 0", color: "#28a745" }}>Actieve Winkels</h3>
          <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
            {shops.filter(shop => shop.plan === "premium").length}
          </p>
        </div>
        <div style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          textAlign: "center"
        }}>
          <h3 style={{ margin: "0 0 10px 0", color: "#ffc107" }}>Gratis Plan</h3>
          <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
            {shops.filter(shop => shop.plan === "free").length}
          </p>
        </div>
      </div>

      {/* Shops List */}
      <div style={{
        background: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        overflow: "hidden"
      }}>
          <div style={{
            padding: "20px",
            borderBottom: "1px solid #e9ecef",
            background: "#f8f9fa"
          }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ margin: 0, color: "#333" }}>{viewMode === "pending" ? "Aanmeldingen" : "Alle Winkels"}</h2>
            {viewMode === "pending" && (
              <span style={{ color: "#17a2b8" }}>Winkels in afwachting van goedkeuring</span>
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: "40px", textAlign: "center" }}>
            <p>Winkels laden...</p>
          </div>
        ) : shops.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
            <p>Nog geen winkels gevonden.</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#e9ecef" }}>
                  <th style={{ padding: "12px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Winkel Naam</th>
                  <th style={{ padding: "12px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Email</th>
                  <th style={{ padding: "12px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Eigenaar</th>
                  <th style={{ padding: "12px", textAlign: "center", borderBottom: "1px solid #ddd" }}>Plan</th>
                  <th style={{ padding: "12px", textAlign: "center", borderBottom: "1px solid #ddd" }}>Klanten</th>
                  <th style={{ padding: "12px", textAlign: "center", borderBottom: "1px solid #ddd" }}>Stempels Nodig</th>
                  <th style={{ padding: "12px", textAlign: "center", borderBottom: "1px solid #ddd" }}>Acties</th>
                </tr>
              </thead>
              <tbody>
                {shops.map((shop) => (
                  <tr key={shop.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: "12px" }}>{shop.name || "Onbekend"}</td>
                    <td style={{ padding: "12px" }}>{shop.email || "Onbekend"}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <span style={{
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        background: shop.plan === "premium" ? "#28a745" : "#6c757d",
                        color: "white"
                      }}>
                        {shop.plan === "premium" ? "Premium" : "Gratis"}
                      </span>
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {shop.customerCount || 0}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {shop.stampsRequired || 10}
                    </td>
                    <td style={{ padding: "12px" }}>{shop.email || "Niet gekoppeld"}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "5px", justifyContent: "center", flexWrap: "wrap" }}>
                        {viewMode === "pending" && (
                          <button
                            onClick={() => approveShop(shop)}
                            style={{
                              padding: "6px 12px",
                              background: "#28a745",
                              color: "white",
                              border: "none",
                              borderRadius: "3px",
                              cursor: "pointer",
                              fontSize: "12px"
                            }}
                            title="Goedkeuren"
                          >
                            ✔️ Goedkeuren
                          </button>
                        )}
                        <button
                          onClick={() => openOwnerModal(shop)}
                          style={{
                            padding: "6px 12px",
                            background: "#17a2b8",
                            color: "white",
                            border: "none",
                            borderRadius: "3px",
                            cursor: "pointer",
                            fontSize: "12px"
                          }}
                          title="Koppel eigenaar (zoek op e‑mail/naam)"
                        >
                          👤 Koppel eigenaar
                        </button>
                        <button
                          onClick={() => selectShop(shop)}
                          style={{
                            padding: "6px 12px",
                            background: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "3px",
                            cursor: "pointer",
                            fontSize: "12px"
                          }}
                          title="Beheer Winkel"
                          aria-label={`Beheer winkel ${shop.name || shop.id}`}
                        >
                          ⚙️ Beheer
                        </button>
                        <button
                          onClick={() => toggleShopPlan(shop.id, shop.name, shop.plan || "free")}
                          style={{
                            padding: "6px 12px",
                            background: shop.plan === "premium" ? "#ffc107" : "#28a745",
                            color: "white",
                            border: "none",
                            borderRadius: "3px",
                            cursor: "pointer",
                            fontSize: "12px"
                          }}
                          title={shop.plan === "premium" ? "Wijzig naar Gratis" : "Wijzig naar Premium"}
                          aria-label={`Wijzig plan van ${shop.name || shop.id} naar ${shop.plan === "premium" ? "gratis" : "premium"}`}
                        >
                          {shop.plan === "premium" ? "⬇️ Gratis" : "⬆️ Premium"}
                        </button>
                        <button
                          onClick={() => openCustomPricing(shop)}
                          style={{
                            padding: "6px 12px",
                            background: "#6c757d",
                            color: "white",
                            border: "none",
                            borderRadius: "3px",
                            cursor: "pointer",
                            fontSize: "12px"
                          }}
                          title="Custom Pricing"
                          aria-label={`Open custom pricing voor ${shop.name || shop.id}`}
                        >
                          💰 Custom
                        </button>
                        <button
                          onClick={() => deleteShop(shop.id, shop.name)}
                          style={{
                            padding: "6px 12px",
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "3px",
                            cursor: "pointer",
                            fontSize: "12px"
                          }}
                          title="Verwijder winkel en alle gerelateerde data (cascade)"
                          aria-label={`Cascade verwijder winkel ${shop.name || shop.id}`}
                        >
                          🧹 Verwijderen (cascade)
                        </button>
                        <button
                          onClick={() => assignOwnerToShop(shop.id, shop.name || shop.email || shop.id)}
                          style={{
                            padding: "6px 12px",
                            background: "#17a2b8",
                            color: "white",
                            border: "none",
                            borderRadius: "3px",
                            cursor: "pointer",
                            fontSize: "12px"
                          }}
                          title="Koppel eigenaar (op e‑mail)"
                        >
                          👤 Koppel eigenaar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    <ConfirmDialog
      open={!!confirmState}
      title={confirmState?.title}
      message={confirmState?.message}
      danger={confirmState?.danger}
      requireTypedText={confirmState?.requireTypedText}
      confirmLabel={confirmState?.confirmLabel}
      onConfirm={confirmState?.onConfirm}
      onCancel={() => setConfirmState(null)}
    />
    <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default SuperAdminDashboard; 